/**
 * Session Report Types
 * Domain types for the Sessions Report feature
 */

import type {
  LocationUsageType,
  StartSourceType,
  SessionHealthType,
} from './filter-enums';
import type { BreakDownItem } from './api-types';

/**
 * Session grid row data - displayed in the data grid
 * Maps Flutter fields to BackOffice API fields
 */
export interface SessionGridRow {
  // Matching Flutter fields (from session_grid_row.dart)
  locationId: number;           // API: actorId
  sessionId: number;            // API: chargeSessionId
  kioskActorHierarchy: string;  // API: actorHierarchy
  locationName: string;         // API: machineName
  startTime: string;            // API: deviceLocalStartDate
  stopTime: string | null;      // API: deviceLocalStopDate
  totalEnergy: number | null;   // API: totalEnergy
  settlementValue: number | null; // API: totalPaymentAfterDiscountIncVat (Total cost including all fees, VAT, and after discounts)
  isVirtualSession: boolean;    // API: isVirtualSession

  // Extra fields from API needed for detail view
  machineId: number;
  connectorId: number;
  paymentId: number | null;
  durationInSeconds: number | null;
  currencyName: string | null;
  cost: number | null;
  vatCost: number | null;

  // Additional fields for extended grid columns
  meterStart: number | null;
  meterStop: number | null;
  stopReason: string | null;
  startSource: string | null;
  accountName: string | null;
  cardType: string | null;
  rfid: string | null;

  // Fee breakdowns (for visual group headers matching Flutter)
  connectionFee: number | null;              // Connection Fee (Incl VAT)
  connectionFeeWithoutVat: number | null;    // Connection Fee (Excl VAT)
  connectionFeeVat: number | null;           // Connection Fee VAT

  timeFee: number | null;                    // Time Fee (Incl VAT)
  timeFeeWithoutVat: number | null;          // Time Fee (Excl VAT)
  timeFeeVat: number | null;                 // Time Fee VAT

  idleFee: number | null;                    // Idle Fee (Incl VAT)
  idleFeeWithoutVat: number | null;          // Idle Fee (Excl VAT)
  idleFeeVat: number | null;                 // Idle Fee VAT

  // Total payment breakdown
  totalPaymentCostExcVat: number | null;     // Total payment (Excl VAT)
  totalPaymentVat: number | null;            // Total payment VAT
  totalPaymentCostIncVat: number | null;     // Total payment (Incl VAT)

  vatRate: number | null;
  machineUsage: string | null;
  physicalReference: string | null;
  deviceSerial: string | null;
  machineModelName: string | null;    // HIVE-6618: Device type (Amit, ONYX, VPOS Touch, VPOSM4 Mini)
  isOcpi: boolean;
  ocpiEmspName: string | null;

  // NEW columns (from Figma/Flutter)
  nayaxTransactionId: number | null;
  evTierPricing: string | null;        // HIVE-5935: EV Tier Pricing - tariff period (e.g., "שפל\פסגה" = Off-Peak/Peak)
  accountExternalCode: string | null;  // API: externalCode
  isExternalPayment: boolean | null;   // API: isExternalPayment - Shows if account uses external payment
  accountId: number | null;            // HIVE-6307: Account ID column
  tokenId: string | null;              // API: idToken
  tokenDisplayNumber: string | null;   // API: rfid (same as rfid, aliased for clarity)
  sparkMachineId: number | null;
  deviceNumber: string | null;         // API: deviceSerial (aliased)
  prepaidCard: string | null;          // API: prepaidCardDisplayNumber - Prepaid card display number
  chargePointId: number | null;        // API: machineId (aliased)
  chargePointNumber: string | null;    // API: machineOperatorIdentifier

  // Community/Discount fields
  communityName: string | null;        // API: communityName (format: "Community - {name}")
  communityValue: string | null;       // API: communityValue (formatted with strategy: "Energy Amount - 5.5 per kWh")

  // Discount amounts (how much is saved)
  discountExcVat: number | null;       // Discount amount exc VAT
  discountVat: number | null;          // VAT amount saved by discount
  discountIncVat: number | null;       // Total discount inc VAT

  // After-discount payment amounts
  totalPaymentAfterDiscountExcVat: number | null;  // Total payment exc VAT after discount
  totalPaymentAfterDiscountVat: number | null;     // VAT amount after discount
  totalPaymentAfterDiscountIncVat: number | null;  // Total payment inc VAT after discount

  // Buy Info fields (HIVE-6234: matching Flutter)
  energyBuyCost: number | null;        // Utility energy cost (what operator pays to buy energy)
  energyProfit: number | null;         // Profit from energy sales
  totalProfit: number | null;          // Total profit from all fees

  // Tariff levels (L1-L9) - shown in Fees checkbox like Flutter
  l1UnitCost: number | null;
  l1Energy: number | null;
  l1Cost: number | null;
  l2UnitCost: number | null;
  l2Energy: number | null;
  l2Cost: number | null;
  l3UnitCost: number | null;
  l3Energy: number | null;
  l3Cost: number | null;
  l4UnitCost: number | null;
  l4Energy: number | null;
  l4Cost: number | null;
  l5UnitCost: number | null;
  l5Energy: number | null;
  l5Cost: number | null;
  l6UnitCost: number | null;
  l6Energy: number | null;
  l6Cost: number | null;
  l7UnitCost: number | null;
  l7Energy: number | null;
  l7Cost: number | null;
  l8UnitCost: number | null;
  l8Energy: number | null;
  l8Cost: number | null;
  l9UnitCost: number | null;
  l9Energy: number | null;
  l9Cost: number | null;
}

/**
 * Session detail data - displayed in the detail blade
 * Extends grid row with additional fields
 */
export interface SessionDetail extends SessionGridRow {
  // Payment fields (from API or sessionbreakdown)
  last4Digits?: string;
  authTime?: string;
  settlementTime?: string;

  // Charging fields
  connectorType?: string;
  updated: string;

  // Breakdown items from sessionbreakdown endpoint (completed sessions)
  breakdownItems?: BreakDownItem[];

  // Tariff fields (from sessionbreakdown endpoint)
  tariffId?: number;
  tariffDescription?: string;
  priceComponents?: number;
  validFrom?: string;
  validTo?: string;
  restrictions?: string;

  // Extra API fields
  meterStart: number | null;
  meterStop: number | null;
  connectionFee: number | null;
  timeFee: number | null;
  idleFee: number | null;
  stopReason: string | null;
  calculationMethod: string | null;
  accountName: string | null;
  vatRate: number | null;
  totalPaymentCostIncVat: number | null;
}

/**
 * Session filters for the grid
 * Matches Flutter's SessionFilters enum and subjects
 * Updated per HIVE-6245 requirements
 */
export interface SessionFilters {
  // Existing filters
  actorIds?: number[];
  dateFrom: string;
  dateTo: string;
  creditCardLast4?: string;
  groupByLocation: boolean;

  // Text filters
  sessionId?: number;
  chargingStation?: string;           // HIVE-6245 #6: Combined CP Name + CP Number search
  physicalReference?: string;         // HIVE-6245 #7: Physical Reference filter
  connectorId?: number;
  nayaxTransactionId?: number;        // AKA Payment ID (HIVE-6245 #8)
  accountName?: string;
  tokenId?: string;

  // DEPRECATED: Use chargingStation instead
  chargePointName?: string;           // Flutter: cpNameSubject -> machineName
  chargePointNumber?: string;         // Flutter: cpSNSubject -> machineOperatorIdentifier

  // Multi-select dropdown filters (HIVE-6245 #2, #3)
  locationUsage?: LocationUsageType[];  // Changed to array for multi-select
  startSources?: StartSourceType[];     // Multi-select
  deviceTypes?: string[];               // HIVE-6618: Multi-select device type filter (supports any string)

  // Single-select dropdown filters
  paymentMethod?: string;             // Flutter: paymentMethodSubject (CardType)
  sessionHealth?: SessionHealthType;  // Flutter: sessionHealthSubject

  // Live transactions toggle
  liveTransactions?: boolean;         // Flutter: showLiveTransactionsSubject

  // HIVE-6307: Last 4 Digits filter
  last4Digits?: string;
}

/**
 * Default filters - last week (matching Flutter default)
 */
export function getDefaultFilters(): SessionFilters {
  const now = new Date();
  const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  return {
    dateFrom: lastWeek.toISOString().split('T')[0],
    dateTo: now.toISOString().split('T')[0],
    groupByLocation: false,
    liveTransactions: false,
  };
}
